"use client";

import { useState, useEffect } from "react";
import { Sparkles } from "lucide-react";

/**
 * Transformations Section Component
 * Showcases clinic results through three embedded Instagram reels laid out
 * side by side (center reel is dynamic/fetched, left and right reels are fixed).
 */
export function Transformations() {
  // State for the center Instagram Reel URL, defaults to a specific reel if not fetched
  const [reelUrl, setReelUrl] = useState(
    "https://www.instagram.com/reel/DOWRvF7jMFx/embed"
  );

  // Fixed reels shown on either side of the center reel
  const leftReelUrl = "https://www.instagram.com/reel/DYjQL9fSVwO/embed";
  const rightReelUrl = "https://www.instagram.com/reel/DOiNqFcjHpH/embed";

  /**
   * Formats a standard Instagram URL into an embeddable format.
   * Ensures the URL ends with /embed for proper iframe display.
   */
  const getEmbedUrl = (url: string) => {
    if (!url) return "";
    if (url.includes("/embed")) return url;
    const cleanUrl = url.split("?")[0];
    return cleanUrl.endsWith("/") ? `${cleanUrl}embed` : `${cleanUrl}/embed`;
  };

  // Fetch the latest reel URL from the settings API on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch("/api/instagram");
        if (response.ok) {
          const data = await response.json();
          if (data.instagramReelUrl) {
            setReelUrl(getEmbedUrl(data.instagramReelUrl));
          }
        }
      } catch (error) {
        console.error("Failed to fetch settings:", error);
      }
    };
    fetchSettings();
  }, []);

  return (
    <section
      id="transformations"
      className="aesthetic-gradient relative py-12 lg:py-16"
      aria-labelledby="transformations-title"
    >
      {/* Blend the top edge of this section into whatever sits above it */}
      <div className="section-blend-top" />

      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="mb-16 text-center">
          <div className="bg-rose/10 text-rose mb-6 inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold tracking-wide uppercase">
            <Sparkles className="h-4 w-4" aria-hidden="true" />
            Featured Transformation
          </div>
          <h2
            id="transformations-title"
            className="mb-6 text-4xl font-bold md:text-5xl"
          >
            See Us <span className="text-golden">In Action</span>
          </h2>
          <p className="text-muted-foreground mx-auto max-w-lg text-lg">
            Watch our expert procedures and witness the incredible
            transformations we achieve for our patients.
          </p>
        </div>

        {/* Desktop Layout: 3 reels side by side. Frame borders are now a
            soft rose/gold gradient ring instead of a hard solid-color
            border, and each card sits inside a blurred glow so the edge
            reads as soft rather than sharply cut. */}
        <div className="hidden items-center gap-8 lg:grid lg:grid-cols-3">
          {/* Left Reel */}
          <div className="lg:col-span-1">
            <div className="soft-glow-border mx-auto max-w-[360px] overflow-hidden rounded-[2rem] bg-gradient-to-br from-rose/40 to-golden/30 p-[3px] shadow-xl">
              <div className="overflow-hidden rounded-[1.9rem] bg-background">
                <div className="relative aspect-[9/16] w-full">
                  <iframe
                    src={leftReelUrl}
                    className="absolute inset-0 h-full w-full"
                    allow="encrypted-media"
                    title="Vadodara's Premiere Aesthetics Destination"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Center Reel */}
          <div className="lg:col-span-1">
            <div className="soft-glow-border mx-auto max-w-[450px] overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-golden/50 to-rose/40 p-1 shadow-2xl">
              <div className="overflow-hidden rounded-[2.3rem] bg-background">
                <div className="relative aspect-[9/16] w-full">
                  <iframe
                    src={reelUrl}
                    className="absolute inset-0 h-full w-full"
                    allow="encrypted-media"
                    title="SkinSio Treatment Procedure Video"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right Reel */}
          <div className="lg:col-span-1">
            <div className="soft-glow-border mx-auto max-w-[360px] overflow-hidden rounded-[2rem] bg-gradient-to-br from-rose/40 to-golden/30 p-[3px] shadow-xl">
              <div className="overflow-hidden rounded-[1.9rem] bg-background">
                <div className="relative aspect-[9/16] w-full">
                  <iframe
                    src={rightReelUrl}
                    className="absolute inset-0 h-full w-full"
                    allow="encrypted-media"
                    title="SkinCare Workshop Archives"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile/Tablet Layout: reels stacked vertically */}
        <div className="space-y-10 lg:hidden">
          <div className="soft-glow-border mx-auto max-w-[400px] overflow-hidden rounded-[2rem] bg-gradient-to-br from-rose/40 to-golden/30 p-[3px] shadow-xl">
            <div className="overflow-hidden rounded-[1.8rem] bg-background">
              <div className="relative aspect-[9/16] w-full">
                <iframe
                  src={leftReelUrl}
                  className="absolute inset-0 h-full w-full"
                  allow="encrypted-media"
                  title="Vadodara's Premiere Aesthetics Destination Mobile"
                  loading="lazy"
                />
              </div>
            </div>
          </div>

          <div className="soft-glow-border mx-auto max-w-[400px] overflow-hidden rounded-[2rem] bg-gradient-to-br from-golden/50 to-rose/40 p-[3px] shadow-xl">
            <div className="overflow-hidden rounded-[1.8rem] bg-background">
              <div className="relative aspect-[9/16] w-full">
                <iframe
                  src={reelUrl}
                  className="absolute inset-0 h-full w-full"
                  allow="encrypted-media"
                  title="SkinSio Treatment Procedure Video Mobile"
                  loading="lazy"
                />
              </div>
            </div>
          </div>

          <div className="soft-glow-border mx-auto max-w-[400px] overflow-hidden rounded-[2rem] bg-gradient-to-br from-rose/40 to-golden/30 p-[3px] shadow-xl">
            <div className="overflow-hidden rounded-[1.8rem] bg-background">
              <div className="relative aspect-[9/16] w-full">
                <iframe
                  src={rightReelUrl}
                  className="absolute inset-0 h-full w-full"
                  allow="encrypted-media"
                  title="SkinCare Workshop Archives Mobile"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Blend the bottom edge of this section into whatever sits below it */}
      <div className="section-blend-bottom" />
    </section>
  );
}
